import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger 
} from '@/components/ui/dialog';
import { 
  Form, 
  FormControl, 
  FormDescription, 
  FormField, 
  FormItem, 
  FormLabel, 
  FormMessage 
} from '@/components/ui/form';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  PlusCircle, 
  Pencil, 
  Trash2, 
  Check, 
  X, 
  Search, 
  RefreshCw, 
  Filter, 
  Loader2 
} from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Expert, ExpertField, ExpertTone, ExpertStyle, ExpertTrait } from '@/models/Expert';
import { expertService } from '@/services/expertService';
import { toast } from '@/components/ui/use-toast';

// Schema cho form chỉnh sửa chuyên gia
const expertFormSchema = z.object({
  id: z.string().optional(),
  name: z.string().min(2, { message: 'Tên phải có ít nhất 2 ký tự' }),
  title: z.string().min(2, { message: 'Chức danh phải có ít nhất 2 ký tự' }),
  field: z.string(),
  avatar: z.string().url({ message: 'URL avatar không hợp lệ' }).optional(),
  description: z.string().min(10, { message: 'Mô tả phải có ít nhất 10 ký tự' }),
  personality: z.object({
    tone: z.string(),
    style: z.string(),
    traits: z.array(z.string()).min(1, { message: 'Phải chọn ít nhất 1 đặc điểm tính cách' })
  }),
  greeting: z.string().min(10, { message: 'Lời chào phải có ít nhất 10 ký tự' }),
  systemPrompt: z.string().min(20, { message: 'System prompt phải có ít nhất 20 ký tự' }),
  promptPrefix: z.string().min(10, { message: 'Prompt prefix phải có ít nhất 10 ký tự' }),
  active: z.boolean()
});

type ExpertFormValues = z.infer<typeof expertFormSchema>;

const ExpertManagementPage: React.FC = () => {
  const { t } = useTranslation();
  const [experts, setExperts] = useState<Expert[]>([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [currentExpert, setCurrentExpert] = useState<Expert | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterField, setFilterField] = useState<string>('all');

  const form = useForm<ExpertFormValues>({
    resolver: zodResolver(expertFormSchema),
    defaultValues: {
      name: '',
      title: '',
      field: ExpertField.GENERAL,
      avatar: '',
      description: '',
      personality: {
        tone: ExpertTone.FRIENDLY,
        style: ExpertStyle.CONVERSATIONAL,
        traits: [ExpertTrait.EMPATHETIC]
      },
      greeting: '',
      systemPrompt: '',
      promptPrefix: '',
      active: true
    }
  });

  // Lấy danh sách chuyên gia
  const fetchExperts = async () => {
    try {
      setLoading(true);
      const data = await expertService.getAllExperts();
      if (data && data.length > 0) {
        setExperts(data);
      } else {
        // Nếu không có dữ liệu, giữ nguyên danh sách hiện tại
        toast({
          title: 'Cảnh báo',
          description: 'Không tìm thấy dữ liệu chuyên gia từ server',
          variant: 'default'
        });
      }
    } catch (error) {
      console.error('Error fetching experts:', error);
      toast({
        title: 'Lỗi',
        description: 'Không thể tải danh sách chuyên gia',
        variant: 'destructive'
      });
      // Giữ nguyên danh sách hiện tại nếu có lỗi
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExperts();
  }, []);

  // Lọc chuyên gia theo tìm kiếm và bộ lọc
  const filteredExperts = experts.filter(expert => {
    const matchesSearch = expert.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         expert.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         expert.description.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesField = filterField === 'all' || expert.field === filterField;
    
    return matchesSearch && matchesField;
  });

  // Mở dialog để thêm chuyên gia mới
  const handleAddExpert = () => {
    form.reset({
      name: '',
      title: '',
      field: ExpertField.GENERAL,
      avatar: '',
      description: '',
      personality: {
        tone: ExpertTone.FRIENDLY,
        style: ExpertStyle.CONVERSATIONAL,
        traits: [ExpertTrait.EMPATHETIC]
      },
      greeting: '',
      systemPrompt: '',
      promptPrefix: '',
      active: true
    });
    setIsEditing(false);
    setCurrentExpert(null);
    setDialogOpen(true);
  };

  // Mở dialog để chỉnh sửa chuyên gia
  const handleEditExpert = (expert: Expert) => {
    form.reset({
      id: expert.id,
      name: expert.name,
      title: expert.title,
      field: expert.field,
      avatar: expert.avatar,
      description: expert.description,
      personality: {
        tone: expert.personality.tone,
        style: expert.personality.style,
        traits: expert.personality.traits
      },
      greeting: expert.greeting,
      systemPrompt: expert.systemPrompt,
      promptPrefix: expert.promptPrefix,
      active: expert.active
    });
    setIsEditing(true);
    setCurrentExpert(expert);
    setDialogOpen(true);
  };

  // Xử lý xóa chuyên gia
  const handleDeleteExpert = async (id: string) => {
    if (window.confirm('Bạn có chắc chắn muốn xóa chuyên gia này không?')) {
      try {
        const success = await expertService.deleteExpert(id);
        if (success) {
          toast({
            title: 'Thành công',
            description: 'Đã xóa chuyên gia thành công',
          });
          // Cập nhật danh sách chuyên gia mà không làm mất dữ liệu
          setExperts(prevExperts => prevExperts.filter(expert => expert.id !== id));
        } else {
          throw new Error('Failed to delete expert');
        }
      } catch (error) {
        console.error('Error deleting expert:', error);
        toast({
          title: 'Lỗi',
          description: 'Không thể xóa chuyên gia',
          variant: 'destructive'
        });
      }
    }
  };

  // Xử lý lưu chuyên gia
  const onSubmit = async (values: ExpertFormValues) => {
    try {
      const expertData: Expert = {
        id: values.id || `expert-${Date.now()}`,
        name: values.name,
        title: values.title,
        field: values.field,
        avatar: values.avatar || '/assets/experts/default.png',
        description: values.description,
        personality: {
          tone: values.personality.tone,
          style: values.personality.style,
          traits: values.personality.traits
        },
        greeting: values.greeting,
        systemPrompt: values.systemPrompt,
        promptPrefix: values.promptPrefix,
        active: values.active,
        createdAt: isEditing && currentExpert ? currentExpert.createdAt : new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      const savedExpert = await expertService.saveExpert(expertData);
      
      toast({
        title: 'Thành công',
        description: isEditing ? 'Đã cập nhật chuyên gia thành công' : 'Đã tạo chuyên gia mới thành công',
      });
      
      setDialogOpen(false);
      
      // Cập nhật danh sách chuyên gia mà không làm mất dữ liệu
      if (isEditing) {
        // Nếu đang chỉnh sửa, chỉ cập nhật chuyên gia đã sửa trong danh sách
        setExperts(prevExperts => 
          prevExperts.map(exp => exp.id === savedExpert.id ? savedExpert : exp)
        );
      } else {
        // Nếu đang thêm mới, thêm chuyên gia mới vào danh sách
        setExperts(prevExperts => [...prevExperts, savedExpert]);
      }
    } catch (error) {
      console.error('Error saving expert:', error);
      toast({
        title: 'Lỗi',
        description: 'Không thể lưu thông tin chuyên gia',
        variant: 'destructive'
      });
    }
  };

  return (
    <div className="container mx-auto py-10">
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle>{t('courses.expertManagement')}</CardTitle>
              <CardDescription>
                Quản lý danh sách chuyên gia cho các bài học
              </CardDescription>
            </div>
            <Button onClick={handleAddExpert}>
              <PlusCircle className="mr-2 h-4 w-4" />
              Thêm chuyên gia
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center space-x-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Tìm kiếm chuyên gia..."
                className="pl-8"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <Select
              value={filterField}
              onValueChange={setFilterField}
            >
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Lọc theo lĩnh vực" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tất cả lĩnh vực</SelectItem>
                {Object.values(ExpertField).map((field) => (
                  <SelectItem key={field} value={field}>
                    {field.charAt(0).toUpperCase() + field.slice(1)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={fetchExperts}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>

          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Chuyên gia</TableHead>
                  <TableHead>Lĩnh vực</TableHead>
                  <TableHead>Tính cách</TableHead>
                  <TableHead>Trạng thái</TableHead>
                  <TableHead className="text-right">Thao tác</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-10">
                      <div className="flex items-center justify-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span>{t("loading", { ns: "common" })}</span>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : filteredExperts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-10">
                      <div className="text-muted-foreground">
                        Không tìm thấy chuyên gia nào
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredExperts.map((expert) => (
                    <TableRow key={expert.id}>
                      <TableCell>
                        <div className="flex items-center space-x-3">
                          <Avatar>
                            <AvatarImage src={expert.avatar} alt={expert.name} />
                            <AvatarFallback>{expert.name.charAt(0)}</AvatarFallback>
                          </Avatar>
                          <div>
                            <div className="font-medium">{expert.name}</div>
                            <div className="text-sm text-muted-foreground">{expert.title}</div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {expert.field.charAt(0).toUpperCase() + expert.field.slice(1)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          <Badge variant="secondary" className="text-xs">
                            {expert.personality.tone}
                          </Badge>
                          <Badge variant="secondary" className="text-xs">
                            {expert.personality.style}
                          </Badge>
                        </div>
                      </TableCell>
                      <TableCell>
                        {expert.active ? (
                          <Badge variant="success" className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300">
                            Hoạt động
                          </Badge>
                        ) : (
                          <Badge variant="destructive" className="bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300">
                            Không hoạt động
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEditExpert(expert)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => handleDeleteExpert(expert.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Dialog thêm/sửa chuyên gia */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {isEditing ? 'Chỉnh sửa chuyên gia' : 'Thêm chuyên gia mới'}
            </DialogTitle>
            <DialogDescription>
              {isEditing 
                ? 'Cập nhật thông tin chuyên gia' 
                : 'Thêm một chuyên gia mới vào hệ thống'}
            </DialogDescription>
          </DialogHeader>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <Tabs defaultValue="basic" className="w-full">
                <TabsList className="grid grid-cols-3 mb-4">
                  <TabsTrigger value="basic">Thông tin cơ bản</TabsTrigger>
                  <TabsTrigger value="personality">Tính cách</TabsTrigger>
                  <TabsTrigger value="prompts">Prompts</TabsTrigger>
                </TabsList>

                <TabsContent value="basic" className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Tên chuyên gia</FormLabel>
                          <FormControl>
                            <Input placeholder="Nhập tên chuyên gia" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="title"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Chức danh</FormLabel>
                          <FormControl>
                            <Input placeholder="Ví dụ: Thiền sư, Giáo sư, Bác sĩ..." {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="field"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Lĩnh vực chuyên môn</FormLabel>
                          <Select
                            onValueChange={field.onChange}
                            defaultValue={field.value}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Chọn lĩnh vực" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {Object.values(ExpertField).map((fieldValue) => (
                                <SelectItem key={fieldValue} value={fieldValue}>
                                  {fieldValue.charAt(0).toUpperCase() + fieldValue.slice(1)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="avatar"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>URL Avatar</FormLabel>
                          <FormControl>
                            <Input placeholder="URL hình ảnh đại diện" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="description"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Mô tả</FormLabel>
                        <FormControl>
                          <Textarea 
                            placeholder="Mô tả ngắn về chuyên gia" 
                            className="min-h-[100px]" 
                            {...field} 
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="active"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center space-x-3 space-y-0 rounded-md border p-4">
                        <FormControl>
                          <input
                            type="checkbox"
                            checked={field.value}
                            onChange={field.onChange}
                            className="h-4 w-4"
                          />
                        </FormControl>
                        <div className="space-y-1 leading-none">
                          <FormLabel>Hoạt động</FormLabel>
                          <FormDescription>
                            Chuyên gia có thể được sử dụng trong hệ thống
                          </FormDescription>
                        </div>
                      </FormItem>
                    )}
                  />
                </TabsContent>

                <TabsContent value="personality" className="space-y-4">
                  <FormField
                    control={form.control}
                    name="personality.tone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Giọng điệu</FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          defaultValue={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Chọn giọng điệu" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {Object.values(ExpertTone).map((tone) => (
                              <SelectItem key={tone} value={tone}>
                                {tone.charAt(0).toUpperCase() + tone.slice(1)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormDescription>
                          Giọng điệu khi chuyên gia trả lời
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="personality.style"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Phong cách</FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          defaultValue={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Chọn phong cách" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {Object.values(ExpertStyle).map((style) => (
                              <SelectItem key={style} value={style}>
                                {style.charAt(0).toUpperCase() + style.slice(1)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormDescription>
                          Phong cách trình bày của chuyên gia
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="personality.traits"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Đặc điểm tính cách</FormLabel>
                        <div className="grid grid-cols-3 gap-2">
                          {Object.values(ExpertTrait).map((trait) => (
                            <div key={trait} className="flex items-center space-x-2">
                              <input
                                type="checkbox"
                                id={trait}
                                checked={field.value.includes(trait)}
                                onChange={(e) => {
                                  const updatedTraits = e.target.checked
                                    ? [...field.value, trait]
                                    : field.value.filter((t) => t !== trait);
                                  field.onChange(updatedTraits);
                                }}
                                className="h-4 w-4"
                              />
                              <label htmlFor={trait} className="text-sm">
                                {trait.charAt(0).toUpperCase() + trait.slice(1)}
                              </label>
                            </div>
                          ))}
                        </div>
                        <FormDescription>
                          Chọn ít nhất một đặc điểm tính cách
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="greeting"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Lời chào</FormLabel>
                        <FormControl>
                          <Textarea 
                            placeholder="Lời chào khi bắt đầu cuộc trò chuyện" 
                            className="min-h-[100px]" 
                            {...field} 
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </TabsContent>

                <TabsContent value="prompts" className="space-y-4">
                  <FormField
                    control={form.control}
                    name="systemPrompt"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>System Prompt</FormLabel>
                        <FormControl>
                          <Textarea 
                            placeholder="Hướng dẫn hệ thống cho AI" 
                            className="min-h-[150px]" 
                            {...field} 
                          />
                        </FormControl>
                        <FormDescription>
                          Hướng dẫn hệ thống cho AI về cách đóng vai chuyên gia này. Có thể sử dụng {'{lessonName}'} và {'{lessonContent}'} để tham chiếu đến tên và nội dung bài học.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="promptPrefix"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Prompt Prefix</FormLabel>
                        <FormControl>
                          <Textarea 
                            placeholder="Tiền tố cho prompt" 
                            className="min-h-[100px]" 
                            {...field} 
                          />
                        </FormControl>
                        <FormDescription>
                          Tiền tố được thêm vào trước câu hỏi của người dùng. Có thể sử dụng {'{lessonName}'} và {'{lessonContent}'} để tham chiếu đến tên và nội dung bài học.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </TabsContent>
              </Tabs>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Hủy
                </Button>
                <Button type="submit">
                  {isEditing ? 'Cập nhật' : 'Tạo mới'}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ExpertManagementPage; 